import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import {
  DOCUMENT_NAME_MAX,
  DOCUMENT_PERMISSIONS,
  documentCan,
  recordRecentDocument,
  useAppendMarkdown,
  useCreateDocumentVersion,
  useDocument,
  useDocumentVersions,
  useDuplicateDocument,
  useRenameDocument,
  useRestoreDocumentVersion,
  useSetDocumentLabels,
  useTrashDocument,
  type WorkspaceDocument,
} from '@/entities/document';
import { useViewer } from '@/entities/viewer';
import { useWorkspaces } from '@/entities/workspace';
import { DocumentAccessDialog } from '@/features/document-access';

import type { EditorStatus } from '@/features/document-editor';
import { BackLink } from '@/features/resource-nav';
import { ResourceLabelChips, ResourceLabelsDialog } from '@/features/resource-labels';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import { useErrorMessage } from '@/shared/i18n';
import { formatDateTime } from '@/shared/lib/datetime';
import { originStateFrom } from '@/shared/lib/origin';
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogActions,
  EmptyState,
  ErrorState,
  IconButton,
  Icons,
  InlineTitleEditor,
  LoadingState,
  useToast,
} from '@/shared/ui';

import { DocumentMenu } from './DocumentMenu';
import styles from './DocumentPage.module.css';

// The editor and its CRDT are the heaviest code of the product: only a page that opens a note pays.
const DocumentEditor = lazy(() => import('@/features/document-editor/DocumentEditor'));

function VersionsDialog({
  document,
  mayEdit,
  onClose,
}: {
  document: WorkspaceDocument;
  mayEdit: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const timeZone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const versions = useDocumentVersions(document.id);
  const create = useCreateDocumentVersion(document.id);
  const restore = useRestoreDocumentVersion(document.id);
  const [restoring, setRestoring] = useState<string | null>(null);
  if (restoring) {
    return (
      <ConfirmDialog
        title={t('documents.versions.restoreTitle')}
        message={t('documents.versions.restoreMessage')}
        confirmLabel={t('documents.versions.restore')}
        pending={restore.isPending}
        onCancel={() => {
          setRestoring(null);
        }}
        onConfirm={() => {
          restore.mutate(restoring, {
            onSettled: () => {
              setRestoring(null);
            },
          });
        }}
      />
    );
  }
  return (
    <Dialog title={t('documents.versions.title')} onClose={onClose} size="lg">
      <p>{t('documents.versions.hint')}</p>
      <FormError error={create.error ?? restore.error} />
      {mayEdit && (
        <Button
          variant="secondary"
          disabled={create.isPending}
          data-testid="version-create"
          onClick={() => {
            create.mutate();
          }}
        >
          {t('documents.versions.create')}
        </Button>
      )}
      {versions.isPending && <LoadingState />}
      {versions.data?.length === 0 && <p>{t('documents.versions.empty')}</p>}
      <ul className={styles.versions}>
        {versions.data?.map((version) => (
          <li key={version.id} data-testid="version-row">
            <span>
              {formatDateTime(version.created_at, i18n.language, timeZone)}
              {' · '}
              {t(`documents.versions.kind.${version.kind}`)}
            </span>
            {mayEdit && (
              <Button
                variant="ghost"
                data-testid="version-restore"
                onClick={() => {
                  setRestoring(version.id);
                }}
              >
                {t('documents.versions.restore')}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

const SAVE_TEXT = {
  connecting: 'documents.editor.status.connecting',
  saving: 'documents.editor.status.saving',
  saved: 'documents.editor.status.saved',
  stalled: 'documents.editor.status.stalled',
  unsynced: 'documents.editor.status.unsynced',
  offline: 'documents.editor.status.offline',
} as const;

/**
 * Markdown into THIS note: the file's text is added at the end. Nothing already written is
 * replaced - the dialog says so before anything happens.
 */
function InsertMarkdownDialog({
  documentId,
  onClose,
}: {
  documentId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const append = useAppendMarkdown(documentId);
  const [file, setFile] = useState<File | null>(null);
  const formId = 'document-insert-form';
  return (
    <Dialog
      title={t('documents.insertTitle')}
      onClose={onClose}
      footer={
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={!file || append.isPending}
            data-testid="document-insert-confirm"
          >
            {t('documents.insertConfirm')}
          </Button>
        </DialogActions>
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          if (!file) return;
          append.mutate(file, { onSuccess: onClose });
        }}
      >
        <p className={styles.insertHint}>{t('documents.insertHint')}</p>
        <input
          type="file"
          accept=".md,.markdown,.txt,text/markdown,text/plain"
          aria-label={t('documents.importFile')}
          data-testid="document-append-file"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
          }}
        />
        <FormError error={append.error} />
      </form>
    </Dialog>
  );
}

/** One note: its title, its status and the editor; everything else is in the menu. */
export function DocumentPage() {
  const { t } = useTranslation();
  const { documentId = '' } = useParams();
  const navigate = useNavigate();
  const here = useLocation();
  const toast = useToast();
  const message = useErrorMessage();
  const document = useDocument(documentId);
  const workspaces = useWorkspaces();
  const rename = useRenameDocument(documentId);
  const duplicate = useDuplicateDocument();
  const trash = useTrashDocument();
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const [renaming, setRenaming] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [dialog, setDialog] = useState<
    'access' | 'versions' | 'delete' | 'insert' | 'labels' | null
  >(null);
  const [status, setStatus] = useState<EditorStatus | null>(null);
  const onStatus = useCallback((next: EditorStatus) => {
    setStatus(next);
  }, []);

  const opened = document.isSuccess;
  useEffect(() => {
    if (viewerId && opened) recordRecentDocument(viewerId, documentId);
  }, [viewerId, opened, documentId]);

  if (document.isPending) return <LoadingState />;
  if (document.isError) {
    const gone = isApiError(document.error) && document.error.status === 404;
    // A deleted note and one the viewer may not see look the same: nothing about it is revealed.
    return gone ? (
      <EmptyState title={t('documents.notFound')}>
        <Link to="/app">{t('common.backHome')}</Link>
      </EmptyState>
    ) : (
      <ErrorState error={document.error} onRetry={() => void document.refetch()} />
    );
  }
  const data = document.data;
  const mayEdit = documentCan(data, DOCUMENT_PERMISSIONS.editContent);
  const canRename = documentCan(data, DOCUMENT_PERMISSIONS.editDetails);
  const canDelete = documentCan(data, DOCUMENT_PERMISSIONS.delete);
  const canManage = documentCan(data, DOCUMENT_PERMISSIONS.manageAccess);
  const isTeam =
    workspaces.data?.find((w) => w.id === data.workspace_id)?.workspace_type === 'team';
  const fallback = {
    to: `/app/workspaces/${data.workspace_id}`,
    label: t('nav.workspace'),
  };
  const others = (status?.participants.length ?? 1) - 1;

  return (
    <div className={styles.page}>
      <nav className={styles.back}>
        <BackLink fallback={fallback} />
      </nav>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          {renaming ? (
            <InlineTitleEditor
              name={data.name}
              label={t('documents.name')}
              className={styles.titleInput ?? ''}
              maxLength={DOCUMENT_NAME_MAX}
              onSave={(name) => rename.mutateAsync(name)}
              onClose={() => {
                setRenaming(false);
              }}
            />
          ) : (
            <h1 className={styles.title} title={data.name} data-testid="document-title">
              {canRename ? (
                <button
                  type="button"
                  className={styles.titleButton}
                  aria-label={t('documents.renameNamed', { name: data.name })}
                  onClick={() => {
                    setRenaming(true);
                  }}
                >
                  {data.name}
                </button>
              ) : (
                data.name
              )}
            </h1>
          )}
        </div>
        <div className={styles.tools}>
          <span
            className={styles.status}
            role="status"
            data-testid="document-status"
            data-save={status?.save}
          >
            {status && !status.canEdit && status.ended === null
              ? t('documents.editor.status.readOnly')
              : status?.ended
                ? t(`documents.editor.ended.${status.ended}`)
                : t(SAVE_TEXT[status?.save ?? 'connecting'])}
          </span>
          {others > 0 && (
            <span
              className={styles.presence}
              data-testid="document-presence"
              title={t('documents.editor.othersHere', { count: others })}
            >
              <Icons.users /> {others}
            </span>
          )}
          <IconButton
            label={t('documents.editor.outline')}
            icon={<Icons.menu />}
            aria-pressed={outlineOpen}
            data-testid="outline-toggle"
            onClick={() => {
              setOutlineOpen((value) => !value);
            }}
          />
          <DocumentMenu
            documentId={data.id}
            mayEdit={mayEdit}
            canManage={canManage}
            canDelete={canDelete}
            actions={{
              onInsertMarkdown: () => {
                setDialog('insert');
              },
              onVersions: () => {
                setDialog('versions');
              },
              onAccess: () => {
                setDialog('access');
              },
              onDelete: () => {
                setDialog('delete');
              },
              ...(canRename &&
                isTeam && {
                  onLabels: () => {
                    setDialog('labels');
                  },
                }),
              onDuplicate: () => {
                duplicate.mutate(data.id, {
                  onSuccess: (copy) => {
                    void navigate(`/app/documents/${copy.id}`, { state: originStateFrom(here) });
                  },
                  onError: (error) => {
                    toast.show({ message: message(error) });
                  },
                });
              },
            }}
          />
        </div>
      </header>
      <ResourceLabelChips labels={data.labels} label={t('resourceLabels.field')} />
      <Suspense fallback={<LoadingState />}>
        <DocumentEditor
          key={data.id}
          documentId={data.id}
          mayEdit={mayEdit}
          outlineOpen={outlineOpen}
          onOutlineClose={() => {
            setOutlineOpen(false);
          }}
          onStatus={onStatus}
        />
      </Suspense>
      {dialog === 'labels' && (
        <DocumentLabelsDialog
          document={data}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'access' && (
        <DocumentAccessDialog
          document={data}
          isTeam={isTeam}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'insert' && (
        <InsertMarkdownDialog
          documentId={data.id}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'versions' && (
        <VersionsDialog
          document={data}
          mayEdit={mayEdit}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'delete' && (
        <ConfirmDialog
          title={t('documents.deleteTitle')}
          message={t('documents.deleteMessage', { name: data.name })}
          confirmLabel={t('documents.delete')}
          pending={trash.isPending}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            trash.mutate(data.id, {
              onSuccess: () => {
                void navigate(`/app/workspaces/${data.workspace_id}`);
              },
              onError: (error) => {
                setDialog(null);
                toast.show({ message: message(error) });
              },
            });
          }}
        />
      )}
    </div>
  );
}

function DocumentLabelsDialog({
  document,
  onClose,
}: {
  document: WorkspaceDocument;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSetDocumentLabels(document.id);
  return (
    <ResourceLabelsDialog
      kind="document"
      title={t('resourceLabels.titleDocument', { name: document.name })}
      workspaceId={document.workspace_id}
      current={document.labels ?? []}
      pending={save.isPending}
      error={save.error}
      onSave={(labelIds) => {
        save.mutate(labelIds, { onSuccess: onClose });
      }}
      onClose={onClose}
    />
  );
}
